/**
 * Map of route names to route specs to add named routing abilities to react-router
 */

import Route from 'route-parser';
import { Map } from 'immutable';
import URI from 'urijs';
import qs from 'query-string';

import config from './config';
import ExtendableError from './lib/ExtendableError';
import delayRepayUrls from '../delay-repay/src/urls';
import { GridPages, PointStages, SeasonStages, CheckoutStages, NREPages,
         DaySaveStages, AccountPages, SmartcardPages, InAppPages, WidgetAuthPages,
         HandOffPages, LoginPages, OtherTicketStages, PromotionPages, NewsletterPages,
         OtherPages, AppName, RoversRangersStages, SearchWidgetPages, SmartCarnetStages,
         DisruptionAlertsPages, Widgets, PortalClaims } from './constants/AppConstants';

const searchPart = "(:origin)/(:destination)/(:outboundDateTime)/(:returnDateTime)/(:adults)/(:children)/(:railcards)";
const seasonSearchPart = "(:origin)/(:destination)/(:startDate)/(:duration)/(:passengerType)(/:railcardCode)";
const openGolfSearchPart = ":origin/:adults/:children(/:railcards)";
const smartCarnetSearchPart = ":origin/:destination/:startingDate";

const urlTable = new Map({
    [ GridPages.SERVICES ]: `/journeys-grid/${searchPart}`,

    [ PointStages.SEARCH ]: '/search',
    [ PointStages.SEARCH_RESULTS ]: `/search-results/:source/${searchPart}`,
    [ PointStages.RESULTS ]: `/journeys/${searchPart}`,
    [ PointStages.CHOOSE_OUTWARD ]: `/journeys-list/${searchPart}`,
    [ PointStages.INIT ]: `/initialise-search/${searchPart}`,

    [ CheckoutStages.DELIVERY ]: '/delivery',
    [ CheckoutStages.PAYMENT ]: '/payment',
    [ CheckoutStages.PAYMENT_AMAZON_PAY ]: '/payment/amazonpay',
    [ CheckoutStages.PAYMENT_CARD_HOSTED ]: '/payment/card/hosted',
    [ CheckoutStages.PAYMENT_CARD_HOSTED_BRAINTREE ]: '/payment/card/hosted-braintree',
    [ CheckoutStages.PAYMENT_CONFIRMATION ]: '/payment-confirmation',
    [ CheckoutStages.AMAZON_PAY_MFA_SUCCESS ]: '/amazonpay-mfa-success',
    [ CheckoutStages.AMAZON_PAY_MFA_FAILURE ]: '/amazonpay-mfa-failure',

    [ SeasonStages.SEARCH ]: '/search/season',
    [ SeasonStages.RESULTS ]: `/season-tickets/${seasonSearchPart}`,
    [ SeasonStages.DETAILS ]: '/season-tickets/details',
    [ SeasonStages.INIT ]: `/initialise-season/${seasonSearchPart}`,

    [ OtherTicketStages.SEARCH ]: '/search/other',
    [ DaySaveStages.SEARCH ]: '/search/other/daysave',
    [ DaySaveStages.RESULTS ]: '/daysave/(:date)/(:origin)/(:adults)/(:children)',
    [ RoversRangersStages.SEARCH ]: '/search/other/roversrangers',
    [ RoversRangersStages.RESULTS ]: '/roversrangers/(:date)/(:ticketType)/(:adults)/(:children)/(:railcards)',
    [ SmartCarnetStages.SEARCH ]: '/search/other/smart-carnet',
    [ SmartCarnetStages.RESULTS ]: `/smart-carnet/${smartCarnetSearchPart}`,

    [ AccountPages.ACCOUNT_DETAILS ]: '/account/details',
    [ AccountPages.ACCOUNT_ADDRESSES ]: '/account/address',
    [ AccountPages.ACCOUNT_CONFIRM_SMARTCARD_REPLACEMENT ]: '/account/confirm-smartcard',
    [ AccountPages.ACCOUNT_PREFERENCE_CENTRE ]: '/account/preferences',
    [ AccountPages.ACCOUNT_PHOTOCARDS_RAILCARDS ]: '/account/photocards-railcards',
    [ AccountPages.ACCOUNT_PAYMENT ]: '/account/payment',
    [ AccountPages.ACCOUNT_ORDERS_REFUNDS ]: '/account/orders',
    [ AccountPages.ACCOUNT_ORDER_DETAILS ]: '/account/orders/:orderId',
    [ AccountPages.ACCOUNT_LINK_ORDER ]: '/account/link-order',
    [ AccountPages.ACCOUNT_REFUND_DETAILS ]: '/account/refunds/:orderId',
    [ AccountPages.ACCOUNT_AMEND_DETAILS ]: '/account/amend/:orderId',
    [ AccountPages.ACCOUNT_ETICKETS ]: '/account/etickets(/:orderId)',
    [ AccountPages.ACCOUNT_STICKETS ]: '/account/stickets',
    [ AccountPages.ACCOUNT_AUTO_DELAY_REPAY ]: '/account/auto-delay-repay',
    [ AccountPages.ONE_CLICK_DELAY_REPAY ]: '/account/one-click-delay-repay',
    [ AccountPages.ACCOUNT_DISRUPTION_ALERTS ]: '/account/disruption-alerts',
    [ AccountPages.ACCOUNT_RESERVATIONS ]: '/account/reservations',
    [ AccountPages.ACCOUNT_HISTORIC_ETICKETS ]: '/account/historic-etickets',

    [ PortalClaims.MY_CLAIMS ]: '/my/claims',

    [ AccountPages.SMARTCARD_APPLY ]: '/account/smartcard-apply',
    [ AccountPages.SMARTCARD_TICKETS ]: '/account/smartcard-tickets',
    [ AccountPages.SMARTCARD_HISTORY ]: '/account/smartcard-history',
    [ AccountPages.SMARTCARD_KEYGO ]: '/account/smartcard-keygo',
    [ AccountPages.SMARTCARD_KEYGO_HISTORY ]: '/account/smartcard-keygo-history',
    [ AccountPages.SMARTCARD_REPLACEMENT ]: '/account/smartcard-replacement',
    [ AccountPages.SMARTCARD_TRANSFER ]: '/account/smartcard-transfer',
    [ AccountPages.SMARTCARD_TRANSFER_RESULTS ]: '/account/smartcard-transfer/results/:photocardNumber/:postcode',
    [ AccountPages.SMARTCARD_TRANSFER_CONFIRMATION ]: '/account/smartcard-transfer/confirmation',
    [ AccountPages.SMARTCARD_LINK ]: '/account/smartcard-link',
    [ AccountPages.SMARTCARD_LINK_CONFIRMATION ]: '/account/smartcard-link-confirmation/:customerId/:choice/:token',
    [ AccountPages.EVOUCHERS ]: '/account/evouchers',
    [ AccountPages.ACCOUNT_SMART_CARNET ]: '/account/smart-carnet',

    [ AccountPages.FLEXI ]: '/account/flexi-season',
    [ AccountPages.BOOK_PASSENGER_ASSIST ]: '/account/book-passenger-assist',
    [ AccountPages.VIEW_BOOKED_ASSISTANCE ]: '/account/view-booked-assistance',
    [ AccountPages.ACCESSIBILITY_PROFILE ]: '/account/view-my-accessibility-profile',

    [ SmartcardPages.SELF_REGISTERED ]: '/smartregister',

    [ PromotionPages.TOC_FAMILY ]: '/promotion/toc-family',
    [ PromotionPages.TOC_FAMILY_VERIFY ]: '/promotion/verify',
    [ PromotionPages.PROMOTION ]: '/promotion/:promotion',
    [ PromotionPages.APPLY_PROMOTION ]: '/promotion/:promotion/:promoCode(/)',

    [ LoginPages.RESET_PASSWORD ]: '/reset-password/:token',
    [ LoginPages.SET_PASSWORD_AND_PREFERENCES ]: '/set-password-and-preferences/:token',

    [ InAppPages.LOGIN ]: '/in-app/login',
    [ InAppPages.REGISTER ]: '/in-app/register',
    [ InAppPages.REGISTER_SMARTCARD ]: '/in-app/register-smartcard',
    [ InAppPages.ACCOUNT ]: '/in-app/account',
    [ InAppPages.ACCOUNT_ORDERS_REFUNDS ]: '/in-app/account/orders',
    [ InAppPages.ONE_CLICK_DELAY_REPAY ]: '/in-app/account/one-click-delay-repay',
    [ InAppPages.AUTO_DELAY_REPAY ]: '/in-app/account/auto-delay-repay',
    [ InAppPages.REDIRECT ]: '/in-app/redirect/*',
    [ InAppPages.SEARCH ]: `/in-app/search(/${searchPart})`,
    [ InAppPages.SEASON_SEARCH ]: `/in-app/season-search/${seasonSearchPart}`,
    [ InAppPages.RESULTS ]: `/in-app/journeys/${searchPart}`,
    [ InAppPages.SEASON_RESULTS ]: `/in-app/season-tickets/${seasonSearchPart}`,
    [ InAppPages.LOGOUT ]: '/in-app/logout',

    [ WidgetAuthPages.LOGIN ]: '/widget-auth/login',
    [ WidgetAuthPages.REGISTER ]: '/widget-auth/register',
    [ WidgetAuthPages.LOGOUT ]: '/widget-auth/logout',

    [ HandOffPages.FARE ]: '/hand-off/fare',

    [ DisruptionAlertsPages.MUTE ]: '/disruption-alerts/mute',
    [ DisruptionAlertsPages.DELETE ]: '/timetable-change/delete',

    [ NREPages.ORDER_LANDING ]: '/nre-order/:orderID/:deviceToken(/)',
    [ NewsletterPages.UNSUBSCRIBE ]: '/customers/unsubscribe/:id/:token',
    [ OtherPages.PAYMENT_LANDING ]: '/order-payment/:orderID/:deviceToken(/)',
    [ SearchWidgetPages.IFRAME ]: '/search-widget/iframe',
    [ Widgets.OPEN_GOLF_FIND_TICKETS ]: `/${config.get('openGolfPathname')}`,
    [ Widgets.OPEN_GOLF_TIMETABLE ]: `/${config.get('openGolfTimetablePathname')}`,
    [ Widgets.OPEN_GOLF_TICKET_SELECTION ]: `/open-ticket-selection/${openGolfSearchPart}`,
    [ Widgets.REFUND_WIDGET ]: '/refund-widget'
}).merge(delayRepayUrls);

function _makeRoutes(urlMap) {
    return urlMap.reduce(
        (out, value, key) => {
            let route = new Route(value);
            route.name = key;
            return out.set(key, route);
        }, new Map());
}

//Turn the map above into a map of route-parser objects
let routes = _makeRoutes(urlTable);

export class RouteError extends ExtendableError {
    name = 'RouteError'
}

export class RouteNotFoundError extends RouteError {
    name = 'RouteNotFoundError'
}

export class RouteResolveError extends RouteError {
    name = 'RouteResolveError'
}

export default {

    /**
     * Set the url map, useful for tests
     */
    setUrls (urlMap) {
        routes = _makeRoutes(urlMap);
    },

    reset () {
        routes = _makeRoutes(urlTable);
    },

    /**
     * Get a route from a route name
     */
    get (name) {
        const route = routes.get(name);
        if (!route) {
            throw new RouteNotFoundError(`Cannot find route ${name}`);
        }
        return route;
    },

    /**
     * Build a path from a route name and params
     */
    reverse (name, params) {
        return this.get(name).reverse(params);
    },

    /**
     * Build a location descriptor from a route name, params, and query
     */
    location(name, params, query, extra) {
        return {
            pathname: this.reverse(name, params),
            query: query,
            ...extra
        };
    },

    /**
     * Find a route given a path
     */
    resolve (path) {
        const matches = routes.filter((route) => route.match(path));
        if (!matches.size) {
            throw new RouteResolveError(`Could not resolve route for path: ${path}`);
        }
        return matches.first();
    },

    hasMatchingRoute (name) {
        const appName = config.get('name');
        const isDelayRepay = delayRepayUrls.has(name);
        if (appName === AppName.BOOKING_ENGINE && !isDelayRepay) {
            return true;
        } else if (appName === AppName.DELAY_REPAY && isDelayRepay) {
            return true;
        }
        return false;
    },

    getUrl (name, params, query) {
        const path = this.reverse(name, params);
        const queryString = query ? `?${ URI.buildQuery(query, false, false) }` : '';
        const isDelayRepay = delayRepayUrls.has(name);
        const host = isDelayRepay ? config.get('delayRepayHost') : config.get('bookingEngineHost');
        return `https://${ host }${ path }${ queryString }`;
    },

    getCurrentHostUrl (name, params, query) {
        const path = this.reverse(name, params);
        const queryString = query ? `?${ URI.buildQuery(query, false, false) }` : '';
        const { hostname, port } = window.location;
        let localEnvironmentPath = '';

        if (hostname === 'localhost') {
            const environment = /^\/([^/]*)\//.exec(location.pathname);
            localEnvironmentPath = `/${environment[1]}`;
        }

        return `https://${ hostname }${ port ? `:${port}` : ''}${ localEnvironmentPath }${ path }${ queryString }`;
    },

    getQuery (name) {
        const parsed = qs.parseUrl(window.location.href);
        return parsed.query[name];
    },

    isCurrentUrlPath(pathname) {
        return window.location.pathname.includes(pathname);
    }
};



// WEBPACK FOOTER //
// ./src/urls.js